<template>
    <div class="tokens-create">
        <slot name="trigger" :open="open">
            <button
                v-t="'crm.tokens.create.trigger'"
                class="button tokens-create__trigger"
                @click.prevent="open"
            />
        </slot>

        <portal to="modals">
            <Modal
                v-if="opened"
                name="tokens-create"
                class="tokens-create__modal"
                :title="$t('crm.tokens.create.title')"
                @close="close"
            >
                <template #content>
                    <Spinner v-if="loading" />
                    <template v-if="token">
                        <transition name="fade-down">
                            <div
                                v-if="token"
                                v-tooltip.top="$t('crm.tokens.clipboard.hint')"
                                class="tokens-create__result"
                                @click="copyToClipboard"
                            >
                                <IconTick />
                                <span class="tokens-create__result-text" v-text="token" />
                            </div>
                        </transition>
                    </template>
                    <template v-else>
                        <form id="customers-members-tokens-create" @submit.prevent="submit">
                            <FormField
                                v-if="schema.name"
                                name="name"
                                :error="error"
                                :label="schema.label"
                                :required="schema.name.required"
                            >
                                <FormInput v-model="values.name" :placeholder="$t('misc.name')" />
                            </FormField>
                            <ErrorList :error="error" />
                        </form>
                    </template>
                </template>
                <template #bottom>
                    <template v-if="token">
                        <button
                            v-t="'misc.close'"
                            class="button button--big button--outlined"
                            @click.prevent="close"
                        />
                        <button
                            v-t="'crm.tokens.clipboard.button'"
                            class="button button--big"
                            @click.prevent="copyToClipboard"
                        />
                    </template>
                    <template v-else>
                        <button
                            v-t="'misc.cancel'"
                            class="button button--big button--outlined"
                            @click.prevent="close"
                        />
                        <button
                            form="customers-members-tokens-create"
                            :disabled="!valid"
                            :class="['button', 'button--big', { 'is-disabled': !valid }]"
                            v-text="$t('misc.save')"
                        />
                    </template>
                </template>
            </Modal>
        </portal>
    </div>
</template>

<script>
import { createCustomersMembersToken, createCustomersToken } from '../../api/tokens';
import Spinner from '@speechki-frontend/shared/ui/Spinner';
import { FormField, FormInput, ErrorList } from '@speechki-frontend/shared/ui/forms';
import { Modal } from '@speechki-frontend/shared/ui/modals';
import { IconTick } from '@speechki-frontend/shared/ui/icons';
import { copyToClipboard } from '@speechki-frontend/shared/helpers/functions';
import { mapActions } from 'vuex';

export default {
    name: 'TokensCreate',
    components: {
        Spinner,
        ErrorList,
        FormField,
        FormInput,
        Modal,
        IconTick,
    },
    props: {
        schema: {
            type: Object,
            required: true,
        },
        customerId: {
            type: [Number, String],
            default: null,
        },
        customerMemberId: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            loading: false,
            error: undefined,
            token: undefined,
            opened: false,
            values: {
                name: null,
            },
        };
    },
    computed: {
        valid() {
            return Object.entries(this.schema).every(
                ([name, field]) => !field.required || !!this.values[name]
            );
        },
    },
    watch: {
        async opened(opened) {
            if (!opened) {
                await this.$nextTick();
                this.token = undefined;
            }
        },
    },
    methods: {
        ...mapActions(['pushToast']),

        open() {
            this.opened = true;
        },
        close() {
            this.opened = false;
            this.$emit('closed');
        },
        reset() {
            this.values = {
                name: null,
            };
        },
        createToken() {
            if (this.customerId) return createCustomersToken(this.customerId, this.values);
            return createCustomersMembersToken(this.customerMemberId, this.values);
        },
        async submit() {
            this.loading = true;
            try {
                const result = await this.createToken();
                this.token = result.token;
                this.$emit('created', result);
                this.reset();
                this.pushToast({
                    id: Date.now(),
                    type: 'success',
                    title: this.$t('crm.tokens.create.success'),
                });
            } catch (err) {
                this.error = err;
                this.pushToast({
                    id: Date.now(),
                    type: 'error',
                    title: this.$t('crm.tokens.create.error'),
                });
                console.log(err);
            } finally {
                this.loading = false;
            }
        },
        copyToClipboard() {
            copyToClipboard(this.token);
            this.pushToast({
                id: Date.now(),
                type: 'success',
                title: this.$t('crm.tokens.clipboard.success'),
            });
        },
    },
};
</script>

<style lang="scss" src="./TokensCreate.scss"></style>
